import { useState, useCallback, useMemo, useEffect } from "react";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import {
  View,
  Text,
  Pressable,
  Modal,
  Platform,
  ActivityIndicator,
  ScrollView,
  TextInput,
  Alert,
  Linking,
} from "react-native";
import { Image } from "expo-image";
import {
  FileText,
  RefreshCw,
  Trash2,
  Calendar,
  Beaker,
  ClipboardList,
  Plus,
  X,
  Save,
  Pill,
  CalendarCheck,
  CalendarClock,
  ExternalLink,
} from "lucide-react-native";
import { BlurView } from "expo-blur";
import {
  useDeleteDocument,
  useGetDownloadUrl,
} from "@src/packages/documents/hooks/useDocuments";
import type {
  Document,
  Upload,
} from "@src/packages/documents/hooks/useDocuments";
import {
  useTestResults,
  useCreateTestResults,
  useUpdateTestResult,
  useDeleteTestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import type {
  TestResult,
  TestResultType,
  BloodTestCategory,
} from "@src/packages/test-results/hooks/useTestResults";
import {
  HORMONAL_MARKERS,
  UNITS,
} from "@src/packages/document-vault/add-document/review-forms/marker-data";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { useAppointments } from "@src/packages/care/hooks/useAppointments";
import {
  AddMedicationModal,
  type EditMedicationData,
} from "./care/modals/AddMedicationModal";
import { AddAppointmentModal } from "./care/modals/AddAppointmentModal";
import type { AppointmentScheduleItem } from "./care/schedule.types";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../onboarding/theme";
import { BrownButton, MoonDetailRow } from "@louise/moon-ui";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// ── Marker & unit options ──

const MARKER_OPTIONS = HORMONAL_MARKERS.map((m) => m.name);

/** Look up default unit for a given marker name */
function defaultUnitForMarker(marker: string): string | null {
  const found = HORMONAL_MARKERS.find(
    (m) => m.name.toLowerCase() === marker.toLowerCase(),
  );
  return found?.defaultUnit ?? null;
}

// ── Types ──

export type ClinicalValue = {
  label: string;
  value: string;
  unit?: string;
  referenceRange?: string;
  flag?: "normal" | "low" | "high" | "critical";
};

export type PreviewDocumentInfo = {
  slotId: string;
  slotName: string;
  slotWhy: string;
  document: Document;
  clinicalValues?: ClinicalValue[];
} | null;

type Props = {
  visible: boolean;
  onClose: () => void;
  documentInfo: PreviewDocumentInfo;
  onReupload: () => void;
};

type EditableRow = {
  id: string;
  /** If this row came from an existing TestResult, store its id here */
  testResultId: string | null;
  marker: string;
  value: string;
  unit: string;
  notes: string;
  dirty: boolean;
  isNew: boolean;
};

function isImageContentType(contentType: string | null): boolean {
  if (!contentType) return false;
  return contentType.startsWith("image/");
}

/**
 * Renders a single uploaded file with an inline image preview (if it's an
 * image) or a filename pill + "Open" link (for PDFs and other types). Used
 * inside DocumentPreviewModal so the user can actually see what they
 * uploaded — previously the modal only showed metadata, no file.
 */
function UploadPreviewCard({ upload }: { upload: Upload }) {
  const capturePress = usePressCapture("UploadPreviewCard");
  const t = useTranslations();
  const getDownloadUrl = useGetDownloadUrl();
  const isImage = isImageContentType(upload.contentType);

  // Always fetch the download URL so we can both render images inline and
  // open PDFs in a new tab. Cached by upload id.
  const { data: downloadUrl = null, isLoading } = useQuery({
    queryKey: ["download-url", upload.id],
    queryFn: async () => {
      const url = await getDownloadUrl.mutateAsync({
        uploadId: upload.id,
        disposition: "attachment",
      });
      return typeof url === "string" ? url : null;
    },
    enabled: !!upload.id,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // "front" / "back" notes come from the insurance-card flow; otherwise undefined.
  const sideLabel = upload.notes?.toLowerCase();
  const sideTitle =
    sideLabel === "front"
      ? t("documents.preview.side_front")
      : sideLabel === "back"
        ? t("documents.preview.side_back")
        : (upload.name ?? t("documents.preview.attachment_fallback"));

  return (
    <View
      style={{
        borderRadius: 12,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
        overflow: "hidden",
        marginBottom: 10,
        backgroundColor: "rgba(0,0,0,0.02)",
      }}
    >
      {(sideLabel === "front" || sideLabel === "back") && (
        <View
          style={{
            paddingHorizontal: 12,
            paddingVertical: 6,
            backgroundColor: "rgba(0,0,0,0.03)",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "600",
              color: EMBER.colors.textMid,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {sideTitle}
          </Text>
        </View>
      )}

      {isImage ? (
        downloadUrl ? (
          <Image
            source={{ uri: downloadUrl }}
            style={{ width: "100%", height: 200 }}
            contentFit="contain"
            cachePolicy="memory-disk"
          />
        ) : isLoading ? (
          <View
            style={{
              height: 80,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ActivityIndicator size="small" color={EMBER.colors.warm} />
          </View>
        ) : null
      ) : (
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                upload.name ?? t("documents.preview.attachment_fallback"),
              ),
              "open_document_attachment",
            );
            if (downloadUrl) Linking.openURL(downloadUrl);
          }}
          disabled={!downloadUrl}
          style={{
            paddingHorizontal: 14,
            paddingVertical: 14,
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          }}
        >
          <FileText size={20} color={EMBER.colors.warm} strokeWidth={1.5} />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.text,
            }}
          >
            {upload.name ?? t("documents.preview.attachment_fallback")}
          </Text>
          {isLoading ? (
            <ActivityIndicator size="small" color={EMBER.colors.warm} />
          ) : (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 4,
              }}
            >
              <ExternalLink
                size={14}
                color={EMBER.colors.warm}
                strokeWidth={2}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "600",
                  color: EMBER.colors.warm,
                }}
              >
                {t("documents.preview.open")}
              </Text>
            </View>
          )}
        </Pressable>
      )}
    </View>
  );
}

function formatFileSize(size: string | null): string {
  if (!size) return "";
  const bytes = parseInt(size, 10);
  if (isNaN(bytes)) return size;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Build editable rows from linked test results */
function testResultsToRows(results: TestResult[]): EditableRow[] {
  return results.map((tr) => ({
    id: tr.id,
    testResultId: tr.id,
    marker: tr.name ?? "",
    value: tr.patientValue ?? tr.value ?? tr.doctorValue ?? "",
    unit: tr.unit ?? "",
    notes: tr.notes ?? "",
    dirty: false,
    isNew: false,
  }));
}

// ── Tag parsing helpers ──
//
// Documents carry tags like:
//   doctype:treatment_plan
//   med:NAME|DOSAGE UNIT|FREQUENCY|START_DATE|TIME_OF_DAY
//   appt:TITLE|TYPE|DATE|TIME
//
// We use them to decide which sections to render (per doctype) and to show
// read-only summaries of the meds / appointments linked to the doc.

export type Doctype =
  | "lab"
  | "treatment_plan"
  | "appointment_letter"
  | "imaging"
  | "genetic"
  | "visit_report"
  | "other";

function parseDoctype(tags: string[]): Doctype {
  const tag = tags.find((t) => t.startsWith("doctype:"));
  if (!tag) return "other";
  const value = tag.slice("doctype:".length);
  switch (value) {
    case "lab":
    case "treatment_plan":
    case "appointment_letter":
    case "imaging":
    case "genetic":
    case "visit_report":
      return value;
    default:
      return "other";
  }
}

export type LinkedMedSummary = {
  name: string;
  dosage: string;
  frequency: string;
  startDate: string;
  timeOfDay: string;
};

export type LinkedAppointmentSummary = {
  title: string;
  type: string;
  date: string;
  time: string;
};

function parseLinkedMeds(tags: string[]): LinkedMedSummary[] {
  return tags
    .filter((t) => t.startsWith("med:"))
    .map((t) => {
      const [name, dosage, frequency, startDate, timeOfDay] = t
        .slice("med:".length)
        .split("|");
      return {
        name: name ?? "",
        dosage: dosage ?? "",
        frequency: frequency ?? "",
        startDate: startDate ?? "",
        timeOfDay: timeOfDay ?? "",
      };
    })
    .filter((m) => m.name.trim().length > 0);
}

function parseLinkedAppointments(tags: string[]): LinkedAppointmentSummary[] {
  return tags
    .filter((t) => t.startsWith("appt:"))
    .map((t) => {
      const [title, type, date, time] = t.slice("appt:".length).split("|");
      return {
        title: title ?? "",
        type: type ?? "",
        date: date ?? "",
        time: time ?? "",
      };
    })
    .filter((a) => a.title.trim().length > 0);
}

// ── Sub-components ──

function sanitizeNumeric(v: string): string {
  return v.replace(/[^0-9.\-]/g, "");
}

function ChipGrid({
  options,
  selected,
  onSelect,
}: {
  options: string[];
  selected: string;
  onSelect: (v: string) => void;
}) {
  const capturePress = usePressCapture("ChipGrid");
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
        paddingVertical: 8,
      }}
    >
      {options.map((option) => {
        const isSelected = option.toLowerCase() === selected.toLowerCase();
        return (
          <Pressable
            key={option}
            onPress={() => {
              capturePress(pressLabel(option), "document_chip");
              onSelect(option);
            }}
            style={{
              paddingHorizontal: 10,
              paddingVertical: 6,
              borderRadius: 16,
              backgroundColor: isSelected
                ? EMBER.colors.warm
                : "rgba(0,0,0,0.04)",
              borderWidth: 1,
              borderColor: isSelected ? EMBER.colors.warm : "rgba(0,0,0,0.06)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: isSelected ? "#fff" : EMBER.colors.textMid,
                fontWeight: isSelected ? "600" : "400",
              }}
            >
              {option}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function ValueRow({
  row,
  onUpdate,
  onRemove,
  onTapMarker,
  onTapUnit,
  isMarkerActive,
  isUnitActive,
}: {
  row: EditableRow;
  onUpdate: (field: keyof EditableRow, value: string) => void;
  onRemove: () => void;
  onTapMarker: () => void;
  onTapUnit: () => void;
  isMarkerActive: boolean;
  isUnitActive: boolean;
}) {
  const capturePress = usePressCapture("ValueRow");
  const t = useTranslations();
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        height: 40,
        backgroundColor:
          isMarkerActive || isUnitActive
            ? `${EMBER.colors.warm}06`
            : "rgba(0,0,0,0.02)",
        borderRadius: 10,
        borderWidth: 1,
        borderColor:
          isMarkerActive || isUnitActive
            ? `${EMBER.colors.warm}25`
            : row.dirty
              ? `${EMBER.colors.warm}25`
              : "rgba(0,0,0,0.05)",
        overflow: "hidden",
      }}
    >
      {/* Marker */}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(row.marker || t("documents.preview.select_placeholder")),
            "pick_result_marker",
          );
          onTapMarker();
        }}
        style={{ flex: 3, paddingHorizontal: 10, justifyContent: "center" }}
      >
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            fontSize: 13,
            fontWeight: "500",
            color: row.marker
              ? isMarkerActive
                ? EMBER.colors.warm
                : EMBER.colors.text
              : EMBER.colors.textFaint,
          }}
        >
          {row.marker || t("documents.preview.select_placeholder")}
        </Text>
      </Pressable>

      <View
        style={{ width: 1, height: 20, backgroundColor: "rgba(0,0,0,0.06)" }}
      />

      {/* Value */}
      <TextInput
        value={row.value}
        onChangeText={(v) => onUpdate("value", sanitizeNumeric(v))}
        placeholder="—"
        placeholderTextColor={EMBER.colors.textFaint}
        keyboardType="decimal-pad"
        style={{
          width: 52,
          fontSize: 15,
          fontWeight: "700",
          color: EMBER.colors.text,
          fontFamily: "DMSans_700Bold",
          textAlign: "center",
          paddingHorizontal: 2,
          paddingVertical: 0,
          ...(Platform.OS === "web" && { outlineStyle: "none" }),
        }}
      />

      <View
        style={{ width: 1, height: 20, backgroundColor: "rgba(0,0,0,0.06)" }}
      />

      {/* Unit */}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(row.unit || t("documents.preview.unit_placeholder")),
            "pick_result_unit",
          );
          onTapUnit();
        }}
        style={{ flex: 2, paddingHorizontal: 8, justifyContent: "center" }}
      >
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            fontSize: 12,
            color: row.unit
              ? isUnitActive
                ? EMBER.colors.warm
                : EMBER.colors.textLight
              : EMBER.colors.textFaint,
          }}
        >
          {row.unit || t("documents.preview.unit_placeholder")}
        </Text>
      </Pressable>

      {/* Remove */}
      <Pressable
        onPress={() => {
          capturePress(pressLabel("remove_result_row"), "remove_result_row");
          onRemove();
        }}
        hitSlop={4}
        style={{ width: 32, alignItems: "center", justifyContent: "center" }}
      >
        <X size={13} color={EMBER.colors.textFaint} strokeWidth={1.5} />
      </Pressable>
    </View>
  );
}

// ── Modal Content ──

function PreviewContent({
  onClose,
  documentInfo,
  onReupload,
}: {
  onClose: () => void;
  documentInfo: PreviewDocumentInfo;
  onReupload: () => void;
}) {
  const capturePress = usePressCapture("PreviewContent");
  const t = useTranslations();
  const { format: formatDate } = useDateFormat();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [resultNotes, setResultNotes] = useState("");
  // CTA modals — opened from the read-only linked sections.
  const [showAddMedication, setShowAddMedication] = useState(false);
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  // When the user taps a linked record we open the corresponding modal in
  // edit mode with the real DB record pre-filled.
  const [editingMedication, setEditingMedication] =
    useState<EditMedicationData | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<
    (AppointmentScheduleItem & { date: string }) | null
  >(null);
  // Active picker: which row + field is showing the chip grid (test results)
  const [activePicker, setActivePicker] = useState<{
    rowId: string;
    field: "marker" | "unit";
  } | null>(null);

  const deleteDocument = useDeleteDocument();
  const getDownloadUrl = useGetDownloadUrl();
  const { data: allTestResults } = useTestResults();
  const createTestResults = useCreateTestResults();
  const updateTestResult = useUpdateTestResult();
  const deleteTestResult = useDeleteTestResult();

  const uploadedUploads = useMemo(
    () =>
      documentInfo?.document.uploads.filter(
        (u) => u.uploadStatus === "uploaded",
      ) ?? [],
    [documentInfo],
  );

  const upload = uploadedUploads[0] ?? null;
  const isReadOnly = documentInfo?.document.readOnly === true;

  const { data: downloadUrl = null, isLoading: loadingUrl } = useQuery({
    queryKey: ["download-url", upload?.id],
    queryFn: async () => {
      if (!upload?.id) return null;
      const url = await getDownloadUrl.mutateAsync({
        uploadId: upload.id,
        disposition: "attachment",
      });
      return typeof url === "string" ? url : null;
    },
    enabled: !!upload?.id,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const handleOpenDocument = useCallback(async () => {
    if (!upload?.id) return;
    try {
      const url =
        downloadUrl ??
        (await getDownloadUrl.mutateAsync({
          uploadId: upload.id,
          disposition: "attachment",
        }));
      if (typeof url === "string" && url.length > 0) {
        await Linking.openURL(url);
      }
    } catch (error) {
      console.error("Open document failed:", error);
    }
  }, [downloadUrl, getDownloadUrl, upload?.id]);

  // Find test results linked to this document's uploads
  const linkedResults = useMemo(() => {
    if (!allTestResults || !documentInfo) return [];
    const uploadIds = new Set(documentInfo.document.uploads.map((u) => u.id));
    return allTestResults.filter(
      (tr) => tr.attachment?.uploadId && uploadIds.has(tr.attachment.uploadId),
    );
  }, [allTestResults, documentInfo]);

  // Seed editable rows from linked results when they load
  useEffect(() => {
    if (linkedResults.length > 0) {
      setRows(testResultsToRows(linkedResults));
      // Collect notes
      const notes = linkedResults
        .filter((tr) => tr.notes)
        .map((tr) => tr.notes!)
        .join("\n\n");
      setResultNotes(notes);
    } else {
      setRows([]);
      setResultNotes("");
    }
    setRemovedIds([]);
  }, [linkedResults]);

  // Parse doctype + linked med/appt summaries from the document's tags.
  // The inline editable forms were replaced with CTAs that open the real
  // AddMedicationModal / AddAppointmentModal. Linked rows themselves are
  // pressable: tapping one opens the same modal in edit mode pre-filled
  // with the live DB record (so the displayed name/dose/date is always
  // whatever the user last saved, not a stale tag snapshot).
  const doctype = useMemo<Doctype>(
    () => parseDoctype(documentInfo?.document.tags ?? []),
    [documentInfo],
  );
  const linkedMedTags = useMemo(
    () => parseLinkedMeds(documentInfo?.document.tags ?? []),
    [documentInfo],
  );
  const linkedAppointmentTags = useMemo(
    () => parseLinkedAppointments(documentInfo?.document.tags ?? []),
    [documentInfo],
  );

  // Fetch live records to enrich the linked summaries.
  const { data: allPatientMeds = [] } = usePatientMedications();
  const appointmentsRange = useMemo(() => {
    // Wide window so we can find any appointment the doc references, past
    // or future, regardless of when the doc was uploaded.
    const now = new Date();
    const start = new Date(now.getFullYear() - 1, 0, 1);
    const end = new Date(now.getFullYear() + 1, 11, 31);
    const toIso = (d: Date) => d.toISOString().slice(0, 10);
    return { startDate: toIso(start), endDate: toIso(end) };
  }, []);
  const { data: allAppointments = [] } = useAppointments(appointmentsRange);

  // For each tag, try to find the matching live record. Match by
  // case-insensitive name (med) or title (appt). When found we render the
  // live data; when not found we fall back to the tag snapshot so the row
  // still shows something.
  type LinkedMedRow = {
    tag: ReturnType<typeof parseLinkedMeds>[number];
    edit: EditMedicationData | null;
  };
  type LinkedAppointmentRow = {
    tag: ReturnType<typeof parseLinkedAppointments>[number];
    edit: (AppointmentScheduleItem & { date: string }) | null;
  };

  const linkedMeds = useMemo<LinkedMedRow[]>(() => {
    return linkedMedTags.map((tag) => {
      const match = allPatientMeds.find((m) => {
        const dbName = (m.medication?.name ?? m.rawMedicationName ?? "")
          .toString()
          .toLowerCase()
          .trim();
        return dbName.length > 0 && dbName === tag.name.toLowerCase().trim();
      });
      if (!match) return { tag, edit: null };
      const edit: EditMedicationData = {
        id: match.id,
        name: match.medication?.name ?? match.rawMedicationName ?? tag.name,
        dose: match.dose ?? null,
        doseUnit: match.doseUnit ?? null,
        route: match.route ?? null,
        form: match.form ?? null,
        timeTaken: Array.isArray(match.timeTaken)
          ? (match.timeTaken as string[])
          : typeof match.timeTaken === "string"
            ? [match.timeTaken]
            : undefined,
        startDate: match.startDate ?? undefined,
        endDate: match.endDate ?? null,
        notes: match.notes ?? null,
      };
      return { tag, edit };
    });
  }, [linkedMedTags, allPatientMeds]);

  const linkedAppointments = useMemo<LinkedAppointmentRow[]>(() => {
    return linkedAppointmentTags.map((tag) => {
      const match = allAppointments.find((a) => {
        const apiTitle = (a.title ?? "").toString().toLowerCase().trim();
        return (
          apiTitle.length > 0 && apiTitle === tag.title.toLowerCase().trim()
        );
      });
      if (!match) return { tag, edit: null };
      // Server returns startDate as ISO datetime — keep only the YYYY-MM-DD
      // part for the date field; pull HH:MM out of startTime / startDate.
      const date = match.startDate?.slice(0, 10) ?? tag.date ?? "";
      const timeIso = /T(\d{2}):(\d{2})/.exec(match.startDate ?? "");
      const time =
        match.startTime?.slice(0, 5) ??
        (timeIso ? `${timeIso[1]}:${timeIso[2]}` : tag.time);
      const edit: AppointmentScheduleItem & { date: string } = {
        type: "appointment",
        id: match.id,
        appointmentId: match.id,
        title: match.title ?? tag.title,
        subtitle: match.meetingRoom ?? "",
        time,
        date,
        icon: CalendarClock,
        consultationType: match.consultationType ?? null,
        location: match.meetingRoom ?? null,
        isOnline: match.isOnline ?? null,
        meetingLink: match.meetingLink ?? null,
        notes: match.description ?? null,
      };
      return { tag, edit };
    });
  }, [linkedAppointmentTags, allAppointments]);

  const hasChanges = useMemo(() => {
    if (removedIds.length > 0) return true;
    return rows.some((r) => r.dirty || r.isNew);
  }, [rows, removedIds]);

  const handleUpdateRow = useCallback(
    (rowId: string, field: keyof EditableRow, value: string) => {
      if (isReadOnly) return;
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId ? { ...r, [field]: value, dirty: true } : r,
        ),
      );
    },
    [isReadOnly],
  );

  const handleRemoveRow = useCallback(
    (rowId: string, testResultId: string | null) => {
      if (isReadOnly) return;
      setRows((prev) => prev.filter((r) => r.id !== rowId));
      if (testResultId) {
        setRemovedIds((prev) => [...prev, testResultId]);
      }
    },
    [isReadOnly],
  );

  const handleAddRow = useCallback(() => {
    if (isReadOnly) return;
    setRows((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        testResultId: null,
        marker: "",
        value: "",
        unit: "",
        notes: "",
        dirty: false,
        isNew: true,
      },
    ]);
  }, [isReadOnly]);

  const handleSave = useCallback(async () => {
    if (isReadOnly) return;
    if (!documentInfo) return;
    setSaving(true);

    try {
      const uploadId =
        documentInfo.document.uploads.find((u) => u.uploadStatus === "uploaded")
          ?.id ?? null;

      // Delete removed rows
      for (const id of removedIds) {
        await deleteTestResult.mutateAsync(id);
      }

      // Update dirty existing rows
      for (const row of rows) {
        if (row.testResultId && row.dirty && !row.isNew) {
          await updateTestResult.mutateAsync({
            testResultId: row.testResultId,
            data: {
              name: row.marker || null,
              patientValue: row.value || null,
              unit: row.unit || null,
            },
          });
        }
      }

      // Create new rows
      const newRows = rows.filter((r) => r.isNew && r.marker.trim());
      if (newRows.length > 0) {
        await createTestResults.mutateAsync(
          newRows.map((row) => ({
            name: row.marker,
            patientValue: row.value || null,
            unit: row.unit || null,
            type: "blood" as TestResultType,
            bloodTestCategory: "hormonal" as BloodTestCategory,
            valueType: "numeric" as const,
            date: documentInfo.document.documentDate || null,
            attachmentId: uploadId,
          })),
        );
      }

      setRemovedIds([]);
      await queryClient.invalidateQueries({ queryKey: ["test-results"] });
      setSaving(false);
    } catch (error) {
      console.error("Save failed:", error);
      setSaving(false);
    }
  }, [
    documentInfo,
    rows,
    removedIds,
    createTestResults,
    updateTestResult,
    deleteTestResult,
    queryClient,
    isReadOnly,
  ]);

  const doDelete = useCallback(async () => {
    if (isReadOnly) return;
    if (!documentInfo) return;
    setDeleting(true);
    try {
      await deleteDocument.mutateAsync(documentInfo.document.id);
      onClose();
    } catch (error) {
      console.error("Delete failed:", error);
      setDeleting(false);
    }
  }, [documentInfo, deleteDocument, isReadOnly, onClose]);

  const handleDelete = useCallback(() => {
    if (isReadOnly) return;
    if (!documentInfo) return;

    const confirmMessage = t("documents.preview.delete_confirm_message", {
      params: { name: documentInfo.slotName },
    });
    if (Platform.OS === "web") {
      const confirmed = window.confirm(confirmMessage);
      if (confirmed) {
        doDelete();
      }
    } else {
      Alert.alert(t("documents.preview.delete_confirm_title"), confirmMessage, [
        { text: t("documents.preview.cancel"), style: "cancel" },
        {
          text: t("documents.preview.delete"),
          style: "destructive",
          onPress: doDelete,
        },
      ]);
    }
  }, [documentInfo, doDelete, isReadOnly, t]);

  if (!documentInfo) return null;

  const doc = documentInfo.document;
  const documentDate = doc.documentDate;
  const uploadDate = upload?.createdAt ?? doc.createdAt ?? null;
  const canEditStructuredResults =
    !isReadOnly &&
    (doctype === "lab" ||
      doctype === "imaging" ||
      doctype === "genetic" ||
      rows.length > 0);

  return (
    <>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ maxHeight: 540 }}
      >
        {/* Close button */}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel("close_document_preview"),
              "close_document_preview",
            );
            onClose();
          }}
          hitSlop={12}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 28,
            height: 28,
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          <Text
            style={{
              fontSize: 18,
              fontWeight: "400",
              color: EMBER.colors.textFaint,
            }}
          >
            ✕
          </Text>
        </Pressable>

        {/* Header */}
        <View style={{ marginBottom: 16, paddingRight: 32 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 20,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {documentInfo.slotName}
          </Text>
          {documentInfo.slotWhy ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textLight,
                marginTop: 4,
                lineHeight: 18,
              }}
            >
              {documentInfo.slotWhy}
            </Text>
          ) : null}
        </View>

        {/* Document details */}
        <View
          style={{
            borderRadius: 12,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(0,0,0,0.02)",
            padding: 14,
            gap: 12,
            marginBottom: 16,
          }}
        >
          {documentDate && (
            <MoonDetailRow
              icon={Calendar}
              label={t("documents.preview.info.record_date")}
              value={formatDate(documentDate)}
            />
          )}
          {doc.name && doc.name !== documentInfo.slotName && (
            <MoonDetailRow
              icon={ClipboardList}
              label={t("documents.preview.info.document_name")}
              value={doc.name}
            />
          )}
          {uploadDate && (
            <MoonDetailRow
              icon={FileText}
              label={t("documents.preview.info.uploaded")}
              value={`${formatDate(uploadDate)}${upload?.size ? ` · ${formatFileSize(upload.size)}` : ""}`}
            />
          )}
        </View>

        {/* File previews — render every uploaded file (images inline, others
            as a filename pill with an Open link). */}
        {uploadedUploads.length > 0 && (
          <View style={{ marginBottom: 16 }}>
            {uploadedUploads.map((u) => (
              <UploadPreviewCard key={u.id} upload={u} />
            ))}
          </View>
        )}

        {/* Editable values list — only for lab/imaging/genetic doctypes where
          structured biomarker rows make sense, plus the legacy case where
          existing test results were already loaded for this doc. Hidden on
          prescriptions and appointment letters: those have their own
          dedicated sections below. */}
        {canEditStructuredResults && (
          <View
            style={{
              borderRadius: 12,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.06)",
              backgroundColor: "rgba(0,0,0,0.02)",
              padding: 14,
              marginBottom: 16,
            }}
          >
            {/* Section header */}
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Beaker size={13} color={EMBER.colors.warm} strokeWidth={1.5} />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    fontWeight: "600",
                    color: EMBER.colors.textLight,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {t("documents.preview.section.results")}
                </Text>
              </View>
              {hasChanges && (
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(t("documents.preview.save")),
                      "documents.preview.save",
                    );
                    handleSave();
                  }}
                  disabled={saving}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    backgroundColor: EMBER.colors.warm,
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 6,
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? (
                    <ActivityIndicator size={12} color="#fff" />
                  ) : (
                    <Save size={12} color="#fff" strokeWidth={2} />
                  )}
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11,
                      fontWeight: "600",
                      color: "#fff",
                    }}
                  >
                    {t("documents.preview.save")}
                  </Text>
                </Pressable>
              )}
            </View>

            {/* Column labels — mirrors ValueRow layout */}
            {rows.length > 0 && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    flex: 3,
                    fontSize: 10,
                    color: EMBER.colors.textFaint,
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                    paddingHorizontal: 10,
                  }}
                >
                  {t("documents.upload.table.marker")}
                </Text>
                <View style={{ width: 1 }} />
                <Text
                  className="font-dm-sans"
                  style={{
                    width: 52,
                    fontSize: 10,
                    color: EMBER.colors.textFaint,
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                    textAlign: "center",
                  }}
                >
                  {t("documents.upload.table.value")}
                </Text>
                <View style={{ width: 1 }} />
                <Text
                  className="font-dm-sans"
                  style={{
                    flex: 2,
                    fontSize: 10,
                    color: EMBER.colors.textFaint,
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                    paddingLeft: 10,
                  }}
                >
                  {t("documents.upload.table.unit")}
                </Text>
                <View style={{ width: 32 }} />
              </View>
            )}

            {/* Rows */}
            <View style={{ gap: 6 }}>
              {rows.map((row) => (
                <ValueRow
                  key={row.id}
                  row={row}
                  onUpdate={(field, value) =>
                    handleUpdateRow(row.id, field, value)
                  }
                  onRemove={() => handleRemoveRow(row.id, row.testResultId)}
                  onTapMarker={() =>
                    setActivePicker(
                      activePicker?.rowId === row.id &&
                        activePicker?.field === "marker"
                        ? null
                        : { rowId: row.id, field: "marker" },
                    )
                  }
                  onTapUnit={() =>
                    setActivePicker(
                      activePicker?.rowId === row.id &&
                        activePicker?.field === "unit"
                        ? null
                        : { rowId: row.id, field: "unit" },
                    )
                  }
                  isMarkerActive={
                    activePicker?.rowId === row.id &&
                    activePicker?.field === "marker"
                  }
                  isUnitActive={
                    activePicker?.rowId === row.id &&
                    activePicker?.field === "unit"
                  }
                />
              ))}
            </View>

            {/* Chip grid — shows below rows when a marker/unit cell is tapped */}
            {activePicker && (
              <ChipGrid
                options={
                  activePicker.field === "marker" ? MARKER_OPTIONS : UNITS
                }
                selected={
                  rows.find((r) => r.id === activePicker.rowId)?.[
                    activePicker.field === "marker" ? "marker" : "unit"
                  ] ?? ""
                }
                onSelect={(v) => {
                  if (activePicker.field === "marker") {
                    handleUpdateRow(activePicker.rowId, "marker", v);
                    const defUnit = defaultUnitForMarker(v);
                    if (defUnit) {
                      const row = rows.find((r) => r.id === activePicker.rowId);
                      if (row && !row.unit) {
                        handleUpdateRow(activePicker.rowId, "unit", defUnit);
                      }
                    }
                  } else {
                    handleUpdateRow(activePicker.rowId, "unit", v);
                  }
                  setActivePicker(null);
                }}
              />
            )}

            {/* Add row */}
            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(t("documents.preview.add_value")),
                  "documents.preview.add_value",
                );
                handleAddRow();
              }}
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                paddingVertical: 10,
                marginTop: rows.length > 0 ? 6 : 0,
                borderRadius: 8,
                borderWidth: 1,
                borderStyle: "dashed",
                borderColor: "rgba(0,0,0,0.08)",
              }}
            >
              <Plus size={14} color={EMBER.colors.warm} strokeWidth={2} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "500",
                  color: EMBER.colors.warm,
                }}
              >
                {t("documents.preview.add_value")}
              </Text>
            </Pressable>
          </View>
        )}

        {/* Linked medications — read-only summary parsed from doc tags. The
          old inline editable form here was incomplete (missing fields vs the
          real AddMedicationModal) and the records it created never landed on
          the schedule reliably. The CTA below opens the real AddMedicationModal. */}
        {(linkedMeds.length > 0 ||
          doctype === "treatment_plan" ||
          doctype === "visit_report") && (
          <View
            style={{
              borderRadius: 12,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.06)",
              backgroundColor: "rgba(0,0,0,0.02)",
              padding: 14,
              marginBottom: 16,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                marginBottom: 10,
              }}
            >
              <Pill size={13} color={EMBER.colors.warm} strokeWidth={1.5} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "600",
                  color: EMBER.colors.textLight,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {t("documents.preview.section.medications")}
              </Text>
            </View>
            {linkedMeds.length === 0 ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  marginBottom: 12,
                }}
              >
                {t("documents.preview.medications_empty")}
              </Text>
            ) : (
              linkedMeds.map(({ tag, edit }, idx) => {
                // Prefer live DB data when we found a matching record so the
                // displayed fields reflect the latest edits, not the original
                // tag snapshot.
                const displayName = edit?.name ?? tag.name;
                const displayDose =
                  edit?.dose != null
                    ? `${edit.dose}${edit.doseUnit ? ` ${edit.doseUnit}` : ""}`
                    : tag.dosage;
                const displayFrequency = edit?.notes ?? tag.frequency;
                const displayTime =
                  (edit?.timeTaken && edit.timeTaken.length > 0
                    ? edit.timeTaken.join(", ")
                    : null) ?? tag.timeOfDay;
                const displayStart = edit?.startDate ?? tag.startDate;
                const subtitle = [displayDose, displayFrequency, displayTime]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <Pressable
                    key={`med-${idx}`}
                    onPress={() => {
                      capturePress(
                        pressLabel(displayName),
                        "edit_medication_row",
                      );
                      (() =>
                        !isReadOnly && edit && setEditingMedication(edit))();
                    }}
                    disabled={isReadOnly || !edit}
                    style={({ pressed }) => ({
                      paddingVertical: 10,
                      borderTopWidth: idx === 0 ? 0 : 1,
                      borderTopColor: "rgba(0,0,0,0.06)",
                      opacity: pressed ? 0.6 : 1,
                    })}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: "600",
                        color: EMBER.colors.text,
                      }}
                    >
                      {displayName}
                    </Text>
                    {subtitle && (
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          color: EMBER.colors.textMid,
                          marginTop: 2,
                        }}
                      >
                        {subtitle}
                      </Text>
                    )}
                    {displayStart && (
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 11,
                          color: EMBER.colors.textLight,
                          marginTop: 2,
                        }}
                      >
                        {t("documents.preview.medications_start", {
                          params: { date: displayStart },
                        })}
                      </Text>
                    )}
                  </Pressable>
                );
              })
            )}
            {!isReadOnly ? (
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(t("documents.preview.add_medication")),
                    "documents.preview.add_medication",
                  );
                  setShowAddMedication(true);
                }}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  marginTop: 10,
                  paddingVertical: 10,
                  borderRadius: 10,
                  borderWidth: 1,
                  borderColor: EMBER.colors.warm,
                  backgroundColor: "rgba(232,109,67,0.06)",
                }}
              >
                <Plus size={14} color={EMBER.colors.warm} strokeWidth={2} />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "600",
                    color: EMBER.colors.warm,
                  }}
                >
                  {t("documents.preview.add_medication")}
                </Text>
              </Pressable>
            ) : null}
          </View>
        )}

        {/* Linked appointments — read-only summary parsed from doc tags. CTA
          opens the real AddAppointmentModal which validates date + time and
          creates a real appointment on the calendar. */}
        {(linkedAppointments.length > 0 ||
          doctype === "appointment_letter" ||
          doctype === "visit_report") && (
          <View
            style={{
              borderRadius: 12,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.06)",
              backgroundColor: "rgba(0,0,0,0.02)",
              padding: 14,
              marginBottom: 16,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                marginBottom: 10,
              }}
            >
              <CalendarCheck
                size={13}
                color={EMBER.colors.warm}
                strokeWidth={1.5}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "600",
                  color: EMBER.colors.textLight,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {t("documents.preview.section.appointments")}
              </Text>
            </View>
            {linkedAppointments.length === 0 ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  marginBottom: 12,
                }}
              >
                {t("documents.preview.appointments_empty")}
              </Text>
            ) : (
              linkedAppointments.map(({ tag, edit }, idx) => {
                const displayTitle = edit?.title ?? tag.title;
                const displayType =
                  (edit?.consultationType && edit.consultationType[0]) ??
                  tag.type;
                const displayDate = edit?.date ?? tag.date;
                const displayTime = edit?.time ?? tag.time;
                const subtitle = [displayType, displayDate, displayTime]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <Pressable
                    key={`appt-${idx}`}
                    onPress={() => {
                      capturePress(
                        pressLabel(displayTitle),
                        "edit_appointment_row",
                      );
                      (() =>
                        !isReadOnly && edit && setEditingAppointment(edit))();
                    }}
                    disabled={isReadOnly || !edit}
                    style={({ pressed }) => ({
                      paddingVertical: 10,
                      borderTopWidth: idx === 0 ? 0 : 1,
                      borderTopColor: "rgba(0,0,0,0.06)",
                      opacity: pressed ? 0.6 : 1,
                    })}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: "600",
                        color: EMBER.colors.text,
                      }}
                    >
                      {displayTitle}
                    </Text>
                    {subtitle && (
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          color: EMBER.colors.textMid,
                          marginTop: 2,
                        }}
                      >
                        {subtitle}
                      </Text>
                    )}
                  </Pressable>
                );
              })
            )}
            {!isReadOnly ? (
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(t("documents.preview.add_appointment")),
                    "documents.preview.add_appointment",
                  );
                  setShowAddAppointment(true);
                }}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  marginTop: 10,
                  paddingVertical: 10,
                  borderRadius: 10,
                  borderWidth: 1,
                  borderColor: EMBER.colors.warm,
                  backgroundColor: "rgba(232,109,67,0.06)",
                }}
              >
                <Plus size={14} color={EMBER.colors.warm} strokeWidth={2} />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "600",
                    color: EMBER.colors.warm,
                  }}
                >
                  {t("documents.preview.add_appointment")}
                </Text>
              </Pressable>
            ) : null}
          </View>
        )}

        {/* Notes / findings */}
        {resultNotes ? (
          <View
            style={{
              borderRadius: 12,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.06)",
              backgroundColor: "rgba(0,0,0,0.02)",
              padding: 14,
              marginBottom: 16,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                fontWeight: "600",
                color: EMBER.colors.textLight,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                marginBottom: 6,
              }}
            >
              {t("documents.preview.section.notes_findings")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textMid,
                lineHeight: 19,
              }}
            >
              {resultNotes}
            </Text>
          </View>
        ) : null}

        {/* Action buttons */}
        <View style={{ gap: 10 }}>
          {upload?.id ? (
            <BrownButton
              label={t("documents.preview.open_document")}
              onPress={handleOpenDocument}
              loading={loadingUrl && !downloadUrl}
              leading={<ExternalLink size={16} color="#fff" strokeWidth={2} />}
            />
          ) : null}

          {!isReadOnly ? (
            <>
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(t("documents.preview.reupload")),
                    "documents.preview.reupload",
                  );
                  onReupload();
                }}
                style={{
                  backgroundColor: upload?.id
                    ? "rgba(0,0,0,0.03)"
                    : EMBER.colors.warm,
                  borderRadius: 12,
                  paddingVertical: 14,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  borderWidth: upload?.id ? 1 : 0,
                  borderColor: "rgba(0,0,0,0.06)",
                }}
              >
                <RefreshCw
                  size={16}
                  color={upload?.id ? EMBER.colors.warm : "#fff"}
                  strokeWidth={2}
                />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 15,
                    fontWeight: "600",
                    color: upload?.id ? EMBER.colors.warm : "#fff",
                  }}
                >
                  {t("documents.preview.reupload")}
                </Text>
              </Pressable>

              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(t("documents.preview.delete")),
                    "documents.preview.delete",
                  );
                  handleDelete();
                }}
                disabled={deleting}
                style={{
                  borderRadius: 12,
                  paddingVertical: 14,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  backgroundColor: "rgba(0,0,0,0.03)",
                  borderWidth: 1,
                  borderColor: "rgba(0,0,0,0.06)",
                  opacity: deleting ? 0.6 : 1,
                }}
              >
                {deleting ? (
                  <ActivityIndicator size="small" color={EMBER.colors.coral} />
                ) : (
                  <>
                    <Trash2
                      size={16}
                      color={EMBER.colors.coral}
                      strokeWidth={2}
                    />
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 15,
                        fontWeight: "600",
                        color: EMBER.colors.coral,
                      }}
                    >
                      {t("documents.preview.delete")}
                    </Text>
                  </>
                )}
              </Pressable>
            </>
          ) : null}
        </View>
      </ScrollView>

      <AddMedicationModal
        visible={
          !isReadOnly && (showAddMedication || editingMedication != null)
        }
        editMedication={editingMedication}
        onClose={() => {
          setShowAddMedication(false);
          setEditingMedication(null);
        }}
      />
      <AddAppointmentModal
        visible={
          !isReadOnly && (showAddAppointment || editingAppointment != null)
        }
        editAppointment={editingAppointment}
        onClose={() => {
          setShowAddAppointment(false);
          setEditingAppointment(null);
        }}
      />
    </>
  );
}

// ── Exported Modal ──

export default function DocumentPreviewModal({
  visible,
  onClose,
  documentInfo,
  onReupload,
}: Props) {
  const capturePress = usePressCapture("DocumentPreviewModal");
  if (!visible) return null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel("dismiss_document_preview"),
            "dismiss_document_preview",
          );
          onClose();
        }}
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(0,0,0,0.4)",
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            width: "90%",
            maxWidth: 420,
            maxHeight: "85%",
            borderRadius: 20,
            overflow: "hidden",
          }}
        >
          {Platform.OS === "web" ? (
            <View
              style={{
                paddingHorizontal: 24,
                paddingTop: 28,
                paddingBottom: 24,
                backgroundColor: "rgba(255,255,255,0.88)",
                backdropFilter: "blur(24px) saturate(1.4)",
                WebkitBackdropFilter: "blur(24px) saturate(1.4)",
              }}
            >
              <PreviewContent
                onClose={onClose}
                documentInfo={documentInfo}
                onReupload={onReupload}
              />
            </View>
          ) : (
            <BlurView
              intensity={80}
              tint="light"
              style={{
                paddingHorizontal: 24,
                paddingTop: 28,
                paddingBottom: 24,
              }}
            >
              <PreviewContent
                onClose={onClose}
                documentInfo={documentInfo}
                onReupload={onReupload}
              />
            </BlurView>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}
